import React, { useState } from 'react';
import { Users, Edit2, Check, Wifi, WifiOff } from 'lucide-react';
import { UserPresence } from '../../services/realtime.ts';

interface LivePresenceBadgeProps {
  currentUser: UserPresence;
  connectedUsers: UserPresence[];
  isConnected: boolean;
  onUpdateName: (name: string) => void;
}

export const LivePresenceBadge: React.FC<LivePresenceBadgeProps> = ({
  currentUser,
  connectedUsers,
  isConnected,
  onUpdateName,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [nameInput, setNameInput] = useState(currentUser.name);

  // Filter unique users or ensure current user is represented
  const displayUsers = connectedUsers.length > 0 ? connectedUsers : [currentUser];

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameInput.trim()) {
      onUpdateName(nameInput.trim());
      setIsEditing(false);
    }
  };

  return (
    <div className="live-presence-container">
      <div className="live-presence-pill" title={isConnected ? 'Connected live to collaborators' : 'Reconnecting...'}>
        <span className={`live-status-dot ${isConnected ? 'online' : 'offline'}`} />
        <span className="live-status-text">
          {isConnected ? (
            <>
              <Wifi size={13} className="inline-icon" />
              <span>{displayUsers.length} live</span>
            </>
          ) : (
            <>
              <WifiOff size={13} className="inline-icon" />
              <span>Connecting...</span>
            </>
          )}
        </span>

        <div className="avatar-stack">
          {displayUsers.slice(0, 4).map(u => {
            const isMe = u.id === currentUser.id;
            const initials = u.name
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()
              .substring(0, 2);

            return (
              <div
                key={u.id}
                className="user-avatar"
                style={{ backgroundColor: u.color }}
                title={`${u.name}${isMe ? ' (You)' : ''}`}
              >
                {initials || 'U'}
              </div>
            );
          })}
        </div>

        {isEditing ? (
          <form onSubmit={handleSave} className="name-edit-form">
            <input
              type="text"
              className="name-edit-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              autoFocus
              onBlur={handleSave}
            />
            <button type="submit" className="name-edit-save-btn">
              <Check size={12} />
            </button>
          </form>
        ) : (
          <button
            className="user-name-btn"
            onClick={() => {
              setNameInput(currentUser.name);
              setIsEditing(true);
            }}
            title="Click to edit your collaborator display name"
          >
            <span className="user-name-label">{currentUser.name}</span>
            <Edit2 size={12} className="edit-icon" />
          </button>
        )}
      </div>
    </div>
  );
};
